import BigNumber from 'bignumber.js';
import { BaseCurrencyEnum } from 'sonata-static-storage/lib/enum/baseCurrency.enum';
import { getPremiumNormalizedBidPriceNumber } from 'sonata-static-storage/lib/helpers/campaignCostFees.helpers';
import { DataFeeEventTypesEnum } from 'sonata-static-storage/lib/schemas/user/userAudienceConfigurationSchema';

import type { LineItemGoal } from 'sonata-static-storage/lib/entities';
import type { PricingModelConfig } from 'sonata-static-storage/lib/schemas/campaign/pricingModel.schema';
import type { DealInterface } from 'sonata-static-storage/lib/schemas/premium/deal.schema';

/** Amount of each currency per 1 USD, as returned by `user/currencyChange/:currency`. */
export type CurrencyChanges = Partial<Record<BaseCurrencyEnum, number>>;

/**
 * Max premium CPM the entity can pay for media, after margins/markup/platform fee and data fees.
 * f-cpm/d-cpm entities use the pmp bid price; performance ones (e.g. a ppv hybrid) estimate the CPM
 * from the overall budget goal, so `goals` must be provided for them.
 */
export function getDealsMaxBidPrice(
  pricingModelConfig: PricingModelConfig,
  dataFeesCPM: number,
  goals: LineItemGoal[]
): number {
  return getPremiumNormalizedBidPriceNumber({
    // The estimate path picks the bid model by `mediaType`; deals are always bought on pmp.
    campaign: { ...pricingModelConfig, goals, mediaType: 'pmp' },
    // Currency is converted on the deal side, so no currency change is applied here.
    currencyChange: 1,
    dataFees: [
      {
        concept: 'wizardDataFeesTotal',
        eventType: DataFeeEventTypesEnum.impressions,
        fee: new BigNumber(dataFeesCPM || 0).dividedBy(1000).toNumber(),
        type: 'wizardDataFeesTotal'
      }
    ]
  }).toNumber();
}

/** Currencies whose change is needed to convert every deal bid floor to the user currency (USD is the base). */
export function getDealsCurrencies(deals: DealInterface[], userCurrency: BaseCurrencyEnum): BaseCurrencyEnum[] {
  return [...new Set([userCurrency, ...deals.map(({ currencyCode }) => currencyCode)])]
    .filter(currency => currency && currency !== BaseCurrencyEnum.USD)
    .sort();
}

/** Keeps the deals whose bid floor, converted to the user currency, does not exceed `maxBidPrice`. */
export function filterDealsByBidFloor({
  currencyChanges,
  deals,
  maxBidPrice,
  userCurrency
}: {
  currencyChanges: CurrencyChanges;
  deals: DealInterface[];
  maxBidPrice: number;
  userCurrency: BaseCurrencyEnum;
}): DealInterface[] {
  const getChange = (currency: BaseCurrencyEnum) =>
    currency === BaseCurrencyEnum.USD ? 1 : (currencyChanges[currency] ?? Number.NaN);

  return deals.filter(({ bidFloor, currencyCode }) => {
    const bidFloorInUserCurrency =
      !currencyCode || currencyCode === userCurrency
        ? new BigNumber(bidFloor)
        : new BigNumber(bidFloor).dividedBy(getChange(currencyCode)).times(getChange(userCurrency));
    return bidFloorInUserCurrency.isLessThanOrEqualTo(maxBidPrice);
  });
}
